import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { Container, Top } from '../../../../../../styles'
import {
  BoxTitle,
  BoxItems,
  Items,
  Circle,
  Content,
  GroupRadios,
  QuestionContent,
  Notice,
  BoxRange,
  BoxSelect,
  BoxStar
} from './styles'
import {
  Breadcrumbs,
  CircularProgress,
  Radio,
  Button,
  InsideFooter,
  Stars,
  TextArea,
  Typography
} from '../../../../../../../../components'

import QuestionMark from '../../../../../../../../assets/Icons/QuestionMark'
import { useModal } from '../../../../../../../../hooks/useModal'
import { MessageModal } from '../../../../../../../../modals/MessageModal'
import { ResponseSentModal } from '../../../../../../../../modals/ResponseSentModal'
import { ResponseSendModal } from '../../../../../../../../modals/ResponseSendModal'
import QuestionMarkWithBorder from '../../../../../../../../assets/Icons/QuestionMarkWithBorder'
import ResponseSentImageQuestionary from '../../../../../../../../assets/Icons/ResponseSentImageQuestionary'
import ResponseSendImageQuestionary from '../../../../../../../../assets/Icons/ResponseSendImageQuestionary'
import { DefaultUserPage } from '../../../../../../../DefaultPages'
import ecbrApi from '../../../../../../../../services/ecbrApi'
import { useQuery } from 'react-query'
import useUrlParam from '../../../../../../../../hooks/useUrlParam'
import { getDate } from '../../../../../../../../utils/dateUtils'
import SelectDefault from '../../../../../../../../components/SelectDefault'
import { QuestionsType } from '../../../../../../../../constants/questions'
import useScrollToTop from '../../../../../../../../hooks/useScrollToTop'
import ErrorMessage from '../../../../../../../../components/Form/ErrorMessage'
import { usePillarNavigation } from './helpers'
import { awaitableForEach } from '../../../../../../../../utils/awaitableForEach'
import {
  ContainerQuestionMarkWithBorder,
  ModalContentText,
  TitleModal,
  ModalBodyContainer
} from '../../../../../../../../modals/styles'

const Questionary = () => {
  const [pillarInfoOpen, setPillarInfoOpen] = useState(false)
  const [answersCache, setAnswersCache] = useState([])
  const [error, setError] = useState('')

  const { scrollToTop } = useScrollToTop()
  const [renderMessageModal, openMessageModal] = useModal(MessageModal)
  const [renderResponseSentModal, openResponseSentModal] =
    useModal(ResponseSentModal)
  const [renderSendResponseModal, openSendResponseModal] =
    useModal(ResponseSendModal)
  const [_brandId, certificateId, pillarId] = useUrlParam(
    '/user/brands/:id/certificates/:id/questionary/:id'
  )

  const showSendResponseModal = useCallback(
    ({ onConfirm, ...rest } = {}) => {
      openSendResponseModal({
        ...rest,
        onConfirm,
        body: (
          <ModalBodyContainer>
            <ContainerQuestionMarkWithBorder>
              <ResponseSendImageQuestionary />
            </ContainerQuestionMarkWithBorder>
            <TitleModal>Atenção!</TitleModal>
            <ModalContentText>
              Após esta operação, não será mais possível editar as respostas.
              Deseja enviar as respostas para correção da sua certificação?
            </ModalContentText>
          </ModalBodyContainer>
        )
      })
    },
    [openSendResponseModal]
  )

  const _showResponseSentModal = useCallback(
    ({ ...params } = {}) => {
      openResponseSentModal({
        ...params,
        body: (
          <ModalBodyContainer>
            <ContainerQuestionMarkWithBorder>
              <ResponseSentImageQuestionary />
            </ContainerQuestionMarkWithBorder>
            <TitleModal>Resposta enviada!</TitleModal>
            <ModalContentText>
              Sua resposta foi enviada e logo processaremos o resultado da sua
              certificação
            </ModalContentText>
          </ModalBodyContainer>
        )
      })
    },
    [openResponseSentModal]
  )

  const showMessageModal = useCallback(
    (message) => {
      openMessageModal({
        body: (
          <ModalBodyContainer>
            <ContainerQuestionMarkWithBorder>
              <QuestionMarkWithBorder />
            </ContainerQuestionMarkWithBorder>
            <TitleModal>O que é?</TitleModal>
            <ModalContentText>{message}</ModalContentText>
          </ModalBodyContainer>
        )
      })
    },
    [openMessageModal]
  )

  const {
    data: certificatePillar,
    isFetching: _isFetchingCertificatePillar,
    isLoading: isLoadingCertificatePillar,
    refetch: refetchCertificatePillar
  } = useQuery(
    'getCertificate',
    async () => {
      try {
        const resp = await ecbrApi.pillars.getUserCertificatePillars(
          certificateId,
          { pillarId }
        )
        const certificateData = resp.data?.data || []
        return certificateData.find(
          (certificate) => certificate.pillarId === Number(pillarId)
        )
      } catch (err) {
        setError(err.message)
      }
    },
    {
      enabled: !!certificateId && !!pillarId,
      cacheTime: 0,
      onSuccess: () => {
        setError('')
      },
      onError: () => {
        setError(
          'Não foi possível carregar os dados. Verifique sua conexão com a internet e atualize a página.'
        )
      }
    }
  )

  const {
    data: answers,
    isFetching: _isFetchingAnswers,
    isLoading: _isLoadingAnswers,
    refetch: refetchAnswers
  } = useQuery(
    'getAnswers',
    async () => {
      try {
        const resp =
          await ecbrApi.questionsAnswers.getAllUserCertificatePillarAnswers(
            certificateId,
            { pillarId }
          )

        return resp?.data || []
      } catch (err) {
        setError(err.message)
      }
    },
    {
      enabled: !!certificatePillar,
      cacheTime: 0,
      refetchInterval: 20000, // 20s
      onSuccess: () => {
        setError('')
      },
      onError: () => {
        setError(
          'Não foi possível carregar os dados. Verifique sua conexão com a internet e atualize a página.'
        )
      }
    }
  )

  const _certificate = useMemo(
    () => certificatePillar?.certificate || {},
    [certificatePillar]
  )

  const owner = useMemo(
    () => certificatePillar?.owner || {},
    [certificatePillar]
  )

  const pillar = useMemo(
    () => certificatePillar?.pillar || {},
    [certificatePillar]
  )

  // Topics com as questions ordenadas por id
  const topics = useMemo(
    () =>
      pillar?.topics
        ?.map((topic) => {
          const questions = topic.questions.sort((a, b) => {
            return a.id - b.id
          })
          return {
            ...topic,
            questions
          }
        })
        .sort((a, b) => {
          return a.id - b.id
        }) || [],
    [pillar]
  )

  const allQuestions = useMemo(
    () => topics.flatMap((topic) => topic.questions),
    [topics]
  )

  const {
    activeTopic,
    setActiveTopic,
    hasNextTopic,
    hasPreviousTopic,
    handleGoToPreviousTopic,
    handleGoToNextTopic
  } = usePillarNavigation(topics)

  const questionsFromActiveTopic = useMemo(
    () =>
      activeTopic?.questions?.map((question) => ({
        ...question,
        answer: answers?.find((answer) => answer.questionId === question.id)
      })) || [],
    [activeTopic, answers]
  )

  const answeredPercentage = certificatePillar?.answeredPercentage

  const lastAnsweredQuestion = useMemo(() => {
    const orderedAnswers = answers?.sort((a, b) =>
      a.createdAt > b.createdAt ? 1 : -1
    )
    return orderedAnswers?.[0] || {}
  }, [answers])

  const isQuestionAnswered = useCallback(
    (q) => answers?.find((a) => a.questionId === q.id),
    [answers]
  )

  useEffect(() => {
    if (!activeTopic && topics?.length) {
      setActiveTopic(topics[0])
    }
  }, [topics, activeTopic, setActiveTopic])

  const handleSaveAllAnsweredQuestions = useCallback(async () => {
    try {
      setError('')
      await awaitableForEach(
        answersCache,
        async ({ questionId, answerValue }) => {
          const question = allQuestions.find((q) => q.id === questionId)

          if (!isQuestionAnswered(question)) {
            const payload = {
              questionId: Number(questionId),
              certificateId: Number(certificateId),
              pillarId: Number(pillarId),
              answer: answerValue.toString()
            }
            await ecbrApi.questionsAnswers.answerQuestion(payload)
          }
        }
      )

      setTimeout(() => {
        refetchCertificatePillar()
        refetchAnswers()
        setAnswersCache([])
        // onAllQuestionsAnswered()
      }, 600)

      return {
        error: false,
        message: ''
      }
    } catch (err) {
      console.log({ err })
      setError(err.message)
      return {
        error: true,
        message: 'Não foi possível salvar as respostas. Tente novamente.'
      }
    }
  }, [
    answersCache,
    allQuestions,
    isQuestionAnswered,
    certificateId,
    pillarId,
    refetchCertificatePillar,
    refetchAnswers
  ])

  const handleChangeAnswer = useCallback(
    (questionId, answerValue, isAnswered) => {
      if (isAnswered) return

      // If answer is empty, remove it from the cache
      if (['', undefined, null].includes(answerValue)) {
        setAnswersCache([
          ...answersCache.filter((a) => a.questionId !== questionId)
        ])
        return
      }

      // Add answer to the cache
      setAnswersCache([
        ...answersCache.filter((a) => a.questionId !== questionId),
        { questionId: Number(questionId), answerValue }
      ])
    },
    [answersCache]
  )

  const getAnswerValue = useCallback(
    (questionId, answer) => {
      return answer
        ? answer.value
        : answersCache.find((a) => a.questionId === questionId)?.answerValue
    },
    [answersCache]
  )

  const handleSaveAnswers = useCallback(async () => {
    showSendResponseModal({
      onConfirm: () => handleSaveAllAnsweredQuestions()
    })
  }, [handleSaveAllAnsweredQuestions, showSendResponseModal])

  return (
    <DefaultUserPage page="brands">
      <Container>
        {renderMessageModal}
        {renderResponseSentModal}
        {renderSendResponseModal}
        <Top>
          <Breadcrumbs />
        </Top>
        <h3>Responda ao questionário para obter a sua certificação</h3>
        <BoxTitle>
          <hgroup>
            <Typography type="mainTitle">
              {pillar && pillar.name}
              {!pillarInfoOpen && (
                <a href="#" onClick={() => setPillarInfoOpen(true)}>
                  + informações
                </a>
              )}
            </Typography>
            {owner && (
              <h4>
                Última resposta: {owner.name} em{' '}
                {getDate(lastAnsweredQuestion.updatedAt)}
              </h4>
            )}
          </hgroup>

          {!isNaN(answeredPercentage) && (
            <CircularProgress
              data={[
                { x: 1, y: answeredPercentage },
                { x: 2, y: 100 - answeredPercentage }
              ]}
              dataPercent={answeredPercentage}
              width="87px"
              height="87px"
            />
          )}
        </BoxTitle>

        <Notice open={pillarInfoOpen}>
          <header>
            <h2>{pillar?.name}</h2>
          </header>
          <main>
            <p>{pillar?.explication}</p>
          </main>
          <footer>
            <Button
              type="button"
              variant="outlinedLinkDefault"
              text="Ocultar"
              width="100px"
              onClick={() => setPillarInfoOpen(false)}
            />
          </footer>
        </Notice>

        {!!topics.length && (
          <BoxItems>
            {topics.map((topic) => (
              <Items
                key={topic.id}
                active={activeTopic?.id === topic.id}
                onClick={() => setActiveTopic(topic)}
              >
                <Circle />
                <h6>{topic.name}</h6>
              </Items>
            ))}
          </BoxItems>
        )}

        <Content>
          <h2>Questionário</h2>

          {isLoadingCertificatePillar && 'Carregando...'}
          {error && <ErrorMessage>{error}</ErrorMessage>}
          {questionsFromActiveTopic.map((question) => {
            const answered = isQuestionAnswered(question)
            return (
              <QuestionContent key={question.id} isAnswered={!!answered}>
                <h3>
                  <span>{question.id}.</span>
                  {/* <div
                    data-for="main"
                    data-tip={`<h2>${question.title}</h2><p>${question.description}</p>`}
                    data-iscapture="true"
                  >
                    <IconForm color="yellow">N</IconForm>
                  </div>
                  <ReactTooltip
                    id="main"
                    place="bottom"
                    type="dark"
                    effect="solid"
                    html={true}
                  /> */}
                </h3>
                <main>
                  <p>{question.description}</p>
                  {!answered && question.explication && (
                    <p
                      className="need-help"
                      onClick={() => showMessageModal(question.explication)}
                    >
                      <QuestionMark color="#BE9137" />
                      <span>Preciso de ajuda</span>
                    </p>
                  )}
                </main>

                {question.type === QuestionsType.MULTIPLE_CHOICE && (
                  <GroupRadios>
                    {question.options?.map((option) => (
                      <li key={option.id}>
                        <Radio
                          label={option.value}
                          name={`${question.id}-answer`}
                          onChange={() =>
                            handleChangeAnswer(question.id, option.id, answered)
                          }
                          checked={
                            Number(getAnswerValue(question.id, answered)) ===
                            option.id
                          }
                          disabled={!!answered}
                        />
                      </li>
                    ))}
                  </GroupRadios>
                )}

                {question.type === QuestionsType.SLIDER && (
                  <BoxRange>
                    <input
                      type="range"
                      min={question.options?.[0]?.value || 0}
                      max={
                        question.options?.[question.options?.length - 1]
                          ?.value || 0
                      }
                      step={1}
                      className="slider"
                      id="myRange"
                      onChange={(e) =>
                        handleChangeAnswer(
                          question.id,
                          e.target.value,
                          answered
                        )
                      }
                      value={getAnswerValue(question.id, answered)}
                      disabled={!!answered}
                    />
                    <ul>
                      {question.options?.map((option) => (
                        <li key={option.id}>
                          <span>{option.value}</span>
                        </li>
                      ))}
                    </ul>
                  </BoxRange>
                )}

                {question.type === QuestionsType.SELECT && (
                  <BoxSelect>
                    <h4>Selecione</h4>
                    <SelectDefault
                      name={question.id}
                      label={question.title}
                      placeholder="Selecione"
                      options={question.options}
                      optionsValue={'id'}
                      optionsLabel={'value'}
                      onChange={(e) =>
                        handleChangeAnswer(
                          question.id,
                          e.target.value,
                          answered
                        )
                      }
                      value={getAnswerValue(question.id, answered)}
                      disabled={!!answered}
                    />
                  </BoxSelect>
                )}

                {question.type === QuestionsType.TEXT && (
                  <BoxSelect fullWidth>
                    <TextArea
                      name={question.id}
                      label={question.title}
                      placeholder="Digite aqui"
                      onChange={(e) =>
                        handleChangeAnswer(
                          question.id,
                          e.target.value,
                          answered
                        )
                      }
                      value={getAnswerValue(question.id, answered)}
                      disabled={!!answered}
                    />
                  </BoxSelect>
                )}

                {question.type === QuestionsType.RATING && (
                  <BoxStar>
                    <h4>Selecione</h4>
                    <Stars
                      onChange={(value) =>
                        handleChangeAnswer(question.id, value, answered)
                      }
                      value={getAnswerValue(question.id, answered)}
                      disabled={!!answered}
                    />
                  </BoxStar>
                )}
              </QuestionContent>
            )
          })}

          <footer>
            <div>
              <Button
                type="button"
                variant="outlinedLinkDefault"
                text="Anterior"
                width="120px"
                disabled={!hasPreviousTopic}
                onClick={handleGoToPreviousTopic(scrollToTop)}
              />
            </div>
            <div>
              <Button
                type="button"
                variant="primary"
                text="Salvar"
                width="120px"
                disabled={!answersCache.length}
                onClick={handleSaveAnswers}
              />
              <Button
                type="button"
                variant="outlinedLinkDefault"
                text="Próximo"
                width="120px"
                disabled={!hasNextTopic}
                onClick={handleGoToNextTopic(scrollToTop)}
              />
            </div>
          </footer>
        </Content>
        <InsideFooter />
      </Container>
    </DefaultUserPage>
  )
}

export default Questionary
